import { ReactNode, useRef, useState } from "react";
import { Box, IconButton, Stack, Tab, Tabs, TextField, Tooltip, Typography } from "@mui/material";
import {
  FormatBold as BoldIcon,
  FormatItalic as ItalicIcon,
  FormatListBulleted as ListaIcon,
  FormatListNumbered as ListaNumIcon,
  Title as TituloIcon,
  Link as LinkIcon,
} from "@mui/icons-material";
import Markdown from "./Markdown";

/**
 * Editor de texto largo en Markdown: una caja grande, botones que insertan la sintaxis
 * y una vista previa con lo que va a leer quien lo reciba.
 *
 * Markdown y no el HTML de `EditorTexto` porque esto lo escribe cualquier evaluador y
 * lo lee el estudiante: se guarda como texto, se lee bien también en un correo, y los
 * comentarios que ya existían —texto plano— son Markdown válido sin migrar nada.
 */
type Formato = { titulo: string; icono: ReactNode; antes: string; despues?: string; linea?: boolean; ejemplo: string };

const FORMATOS: Formato[] = [
  { titulo: "Negrita", icono: <BoldIcon fontSize="small" />, antes: "**", despues: "**", ejemplo: "texto" },
  { titulo: "Cursiva", icono: <ItalicIcon fontSize="small" />, antes: "*", despues: "*", ejemplo: "texto" },
  { titulo: "Subtítulo", icono: <TituloIcon fontSize="small" />, antes: "### ", linea: true, ejemplo: "Subtítulo" },
  { titulo: "Viñetas", icono: <ListaIcon fontSize="small" />, antes: "- ", linea: true, ejemplo: "punto" },
  { titulo: "Lista numerada", icono: <ListaNumIcon fontSize="small" />, antes: "1. ", linea: true, ejemplo: "punto" },
  { titulo: "Enlace", icono: <LinkIcon fontSize="small" />, antes: "[", despues: "](https://)", ejemplo: "texto" },
];

export default function EditorMarkdown({
  value,
  onChange,
  placeholder,
  minRows = 12,
  disabled = false,
}: {
  value: string;
  onChange: (texto: string) => void;
  placeholder?: string;
  minRows?: number;
  disabled?: boolean;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);
  const [vista, setVista] = useState<"escribir" | "previa">("escribir");

  // Envuelve la selección (o un texto de ejemplo) con la sintaxis; en los formatos de
  // línea, la pone al principio de cada línea seleccionada.
  const aplicar = (f: Formato) => {
    const el = ref.current;
    if (!el) return;
    const ini = el.selectionStart;
    const fin = el.selectionEnd;
    const sel = value.slice(ini, fin) || f.ejemplo;
    let nuevo: string;
    let cursorIni: number;
    let cursorFin: number;
    if (f.linea) {
      const inicioLinea = value.lastIndexOf("\n", ini - 1) + 1;
      const bloque = value.slice(inicioLinea, fin) || f.ejemplo;
      const conFormato = bloque
        .split("\n")
        .map((l, n) => (f.antes === "1. " ? `${n + 1}. ` : f.antes) + l)
        .join("\n");
      nuevo = value.slice(0, inicioLinea) + conFormato + value.slice(fin);
      cursorIni = inicioLinea;
      cursorFin = inicioLinea + conFormato.length;
    } else {
      nuevo = value.slice(0, ini) + f.antes + sel + (f.despues ?? "") + value.slice(fin);
      cursorIni = ini + f.antes.length;
      cursorFin = cursorIni + sel.length;
    }
    onChange(nuevo);
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(cursorIni, cursorFin);
    });
  };

  return (
    <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ borderBottom: "1px solid", borderColor: "divider", px: 1, minHeight: 40 }}
      >
        <Tabs value={vista} onChange={(_, v) => setVista(v)} sx={{ minHeight: 40, "& .MuiTab-root": { minHeight: 40, py: 0, textTransform: "none" } }}>
          <Tab value="escribir" label="Escribir" />
          <Tab value="previa" label="Vista previa" />
        </Tabs>
        {vista === "escribir" && (
          <Stack direction="row" spacing={0.25}>
            {FORMATOS.map((f) => (
              <Tooltip key={f.titulo} title={f.titulo}>
                <span>
                  <IconButton size="small" onClick={() => aplicar(f)} disabled={disabled} aria-label={f.titulo}>
                    {f.icono}
                  </IconButton>
                </span>
              </Tooltip>
            ))}
          </Stack>
        )}
      </Stack>
      {vista === "escribir" ? (
        <TextField
          inputRef={ref}
          fullWidth
          multiline
          minRows={minRows}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          disabled={disabled}
          variant="standard"
          InputProps={{ disableUnderline: true }}
          // Arrastrable desde la esquina, para una corrección todavía más larga.
          sx={{ "& .MuiInputBase-root": { p: 1.5, alignItems: "flex-start" }, "& textarea": { resize: "vertical", lineHeight: 1.6 } }}
        />
      ) : (
        <Box sx={{ p: 1.5, minHeight: minRows * 24 }}>
          {value.trim() ? (
            <Markdown texto={value} />
          ) : (
            <Typography variant="body2" color="text.disabled">
              Nada que mostrar todavía.
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
}
